import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button, Input, Label, toast } from '@employer-on-record/ui';
import { companiesApi } from '@/services/api';
import type { CompanyDTO } from '@employer-on-record/shared';

interface Props {
	open: boolean;
	mode: 'create' | 'edit';
	company: CompanyDTO | null;
	onClose: () => void;
}

export function CompanyFormDrawer({ open, mode, company, onClose }: Props) {
	const qc = useQueryClient();
	const [form, setForm] = useState({
		name: '',
		industry: '',
		address: '',
		taxId: '',
		leaveApprovalLevels: 2,
		reimbursementApprovalLevels: 3
	});

	useEffect(() => {
		if (mode === 'edit' && company) {
			setForm({
				name: company.name,
				industry: company.industry ?? '',
				address: company.address ?? '',
				taxId: company.taxId ?? '',
				leaveApprovalLevels: company.leaveApprovalLevels,
				reimbursementApprovalLevels: company.reimbursementApprovalLevels
			});
		} else if (open && mode === 'create') {
			setForm({
				name: '',
				industry: '',
				address: '',
				taxId: '',
				leaveApprovalLevels: 2,
				reimbursementApprovalLevels: 3
			});
		}
	}, [mode, company, open]);

	const submit = useMutation({
		mutationFn: () => {
			const body = {
				name: form.name,
				industry: form.industry || undefined,
				address: form.address || undefined,
				taxId: form.taxId || undefined,
				leaveApprovalLevels: form.leaveApprovalLevels,
				reimbursementApprovalLevels: form.reimbursementApprovalLevels
			};
			return mode === 'create'
				? companiesApi.create(body)
				: companiesApi.update(company!.id, body);
		},
		onSuccess: () => {
			toast(mode === 'create' ? 'Company created' : 'Company updated');
			qc.invalidateQueries({ queryKey: ['admin-companies'] });
			onClose();
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-40 flex">
			<div className="flex-1 bg-black/30" onClick={onClose} />
			<div className="w-[420px] bg-card border-l overflow-y-auto">
				<div className="px-5 py-4 border-b flex items-center">
					<h3 className="font-semibold">{mode === 'create' ? 'Create company' : `Edit ${company?.name}`}</h3>
					<button onClick={onClose} className="ml-auto p-1 rounded hover:bg-background"><X className="h-4 w-4" /></button>
				</div>
				<form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="p-5 grid gap-3">
					<div className="grid gap-1.5">
						<Label htmlFor="name">Name</Label>
						<Input id="name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor="industry">Industry</Label>
						<Input id="industry" value={form.industry} onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))} />
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor="address">Address</Label>
						<Input id="address" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} />
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor="taxId">Tax ID (NPWP)</Label>
						<Input id="taxId" value={form.taxId} onChange={(e) => setForm(f => ({ ...f, taxId: e.target.value }))} />
					</div>
					<div className="border-t mt-2 pt-3 grid gap-3">
						<div className="text-sm font-semibold">Approval workflow</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="grid gap-1.5">
								<Label htmlFor="leaveLevels">Leave levels (1–4)</Label>
								<Input
									id="leaveLevels"
									type="number"
									min={1}
									max={4}
									value={form.leaveApprovalLevels}
									onChange={(e) => setForm(f => ({ ...f, leaveApprovalLevels: parseInt(e.target.value || '0') || 1 }))}
								/>
							</div>
							<div className="grid gap-1.5">
								<Label htmlFor="reimbLevels">Reimbursement levels (1–4)</Label>
								<Input
									id="reimbLevels"
									type="number"
									min={1}
									max={4}
									value={form.reimbursementApprovalLevels}
									onChange={(e) => setForm(f => ({ ...f, reimbursementApprovalLevels: parseInt(e.target.value || '0') || 1 }))}
								/>
							</div>
						</div>
					</div>

					<div className="flex gap-2 mt-4">
						<Button type="button" variant="secondary" onClick={onClose} className="flex-1 justify-center">Cancel</Button>
						<Button type="submit" className="flex-1 justify-center" disabled={submit.isPending}>
							{submit.isPending ? 'Saving…' : mode === 'create' ? 'Create company' : 'Save'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
